/** Shared formatting utilities (UX review D5).
 *
 * Single source of truth for currency, duration, percent and number
 * rendering so tables stop mixing toFixed(2)/toFixed(4)/toLocaleString.
 */
import { format as formatDate } from "date-fns";

/** Format a monetary value with consistent decimals. */
export function formatCurrency(
  value: number | null | undefined,
  decimals = 2
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/** Format a per-minute rate (4 decimals by convention). */
export function formatRate(value: number | null | undefined): string {
  return formatCurrency(value, 4);
}

/** Format duration in seconds as "1h 02m 03s" / "2m 05s" / "12s". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));
  if (s === 0) return "0s";
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}h ${String(m).padStart(2, "0")}m${sec > 0 ? ` ${String(sec).padStart(2, "0")}s` : ""}`;
  }
  return `${m}m${sec > 0 ? ` ${sec}s` : ""}`;
}

/** Format a percentage with fixed decimals. */
export function formatPercent(
  value: number | null | undefined,
  decimals = 1
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toFixed(decimals)}%`;
}

/** Format a plain number with thousands separators. */
export function formatNumber(
  value: number | null | undefined,
  decimals = 0
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Format a timestamp as a short local datetime. */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDate(d, "yyyy-MM-dd HH:mm:ss");
}
